// Cliente PVGIS — hub-api primario, fallback directo a re.jrc.ec.europa.eu (sin clave).
// Caché localStorage 30 días.

import { hubPost, hubConfigurado } from './hub';

const CACHE_PREFIX = 'pvgis:';
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const cacheKey = (lat, lon, kwp, tilt, az) =>
  `${CACHE_PREFIX}${(+lat).toFixed(2)}:${(+lon).toFixed(2)}:${kwp}:${tilt}:${az}`;

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (Date.now() - p.t > CACHE_TTL_MS) return null;
    return p.d;
  } catch { return null; }
}

function writeCache(key, data) {
  try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data })); } catch {}
}

async function fetchDirect({ lat, lon, kwp, losses = 14, tilt = 10, azimuth = 0 }) {
  // PVGIS convention: aspect 0=south, 90=west, -90=east, ±180=north
  const aspect = azimuth - 180;
  const qs = new URLSearchParams({
    lat, lon, peakpower: kwp, loss: losses,
    angle: tilt, aspect,
    outputformat: 'json', mountingplace: 'building',
  });
  const res = await fetch(`https://re.jrc.ec.europa.eu/api/v5_2/PVcalc?${qs}`);
  if (!res.ok) throw new Error(`PVGIS HTTP ${res.status}`);
  const data = await res.json();
  const totals = data.outputs?.totals?.fixed;
  const monthly = data.outputs?.monthly?.fixed || [];
  const annualKwh = totals?.E_y != null ? Number(totals.E_y) : null;
  const monthlyKwh = monthly.map(m => Number(m.E_m || 0));
  const irradiationAnnual = totals?.H_i_y != null ? Number(totals.H_i_y) : null;
  const psh = irradiationAnnual ? +(irradiationAnnual / 365).toFixed(2) : null;
  return { ok: true, annualKwh, monthlyKwh, irradiationAnnual, psh, source: 'pvgis-direct' };
}

export async function fetchPVProduction({ lat, lon, kwp, losses = 14, tilt = 10, azimuth = 0 }) {
  if (!lat || !lon || !kwp) throw new Error('PVGIS: faltan lat/lon/kwp');
  const key = cacheKey(lat, lon, kwp, tilt, azimuth);
  const cached = readCache(key);
  if (cached) return { ...cached, cached: true };

  // hub-api primario → si falla, cae al endpoint público PVGIS (CORS habilitado).
  let data;
  if (hubConfigurado()) {
    try {
      data = await hubPost('pvgis', { lat, lon, kwp, tilt, azimuth, losses });
      if (!data?.annualKwh) throw new Error('Modelo de irradiancia no disponible.');
    } catch (e) {
      data = await fetchDirect({ lat, lon, kwp, losses, tilt, azimuth });
    }
  } else {
    data = await fetchDirect({ lat, lon, kwp, losses, tilt, azimuth });
  }

  if (data?.annualKwh) writeCache(key, data);
  return { ...data, cached: false };
}

// ── TMY horario (Typical Meteorological Year) de PVGIS — 8760 h de irradiancia + temperatura ──────
// Núcleo del diseño "último nivel": serie horaria real del sitio (G(h), Gb(n), Gd(h), T2m) para
// alimentar pvsim.simulateFromHourly. CORS habilitado en re.jrc.ec.europa.eu. Caché 30 días.
const MES_DIAS_ACUM = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
function parseTmyTime(t) {
  // Formatos PVGIS: "20070101:0010" o "2007-01-01T00:10". Devuelve {month, doy, hour}.
  const s = String(t);
  const m = s.match(/^(\d{4})(\d{2})(\d{2}):(\d{2})/) || s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})/);
  if (!m) return null;
  const month0 = Math.max(0, Math.min(11, (+m[2]) - 1));
  const doy = MES_DIAS_ACUM[month0] + (+m[3]);
  return { month: month0, doy, hour: +m[4] };
}

/** Mes (0..11) al que pertenece un día del año. */
function mesDeDoy(doy) {
  for (let m = 11; m >= 0; m--) if (doy > MES_DIAS_ACUM[m]) return m;
  return 0;
}

/**
 * 🔴 El sello de PVGIS es **UTC** (el campo se llama `time(UTC)`) y `simulateFromHourly` interpreta
 * `hour` como **hora solar local**: su ángulo horario es `15·(h−12)`, o sea mediodía solar a las 12.
 * Sin corregir, el modelo cree que amanece a mediodía y la transposición al plano pierde la mitad
 * del recurso — sin dar error, y con el PR intacto porque POA está arriba y abajo de la división.
 * Medido en Villavicencio: 639 kWh/kWp sin corregir contra 1240 corregido.
 */
function aHoraSolar(tm, lon) {
  const corr = Number(lon) / 15;
  if (!Number.isFinite(corr) || corr === 0) return tm;
  let hour = tm.hour + corr;
  let doy = tm.doy;
  if (hour < 0) { hour += 24; doy = doy > 1 ? doy - 1 : 365; }
  else if (hour >= 24) { hour -= 24; doy = doy < 365 ? doy + 1 : 1; }
  return { month: mesDeDoy(doy), doy, hour };
}

/**
 * Deja una fila del TMY en la forma que consume `pvsim.simulateFromHourly`, venga del backend
 * (nuevo o viejo) o de PVGIS directo. Devuelve `null` si no se puede fechar — una fila sin `doy`
 * envenena el motor entero, así que se descarta en vez de propagarse.
 *
 * La corrección de huso se aplica **solo** cuando la hora se dedujo del sello UTC. Si la fila ya
 * trae `doy`/`hour`, viene del backend nuevo, que la entrega en hora solar: volver a corregirla
 * sería desfasarla otra vez.
 */
function normalizarFilaTmy(r, lon) {
  if (!r || typeof r !== 'object') return null;
  const yaSolar = Number.isFinite(Number(r.doy)) && Number.isFinite(Number(r.hour));
  const crudo = yaSolar
    ? { month: Number(r.month) || 0, doy: Number(r.doy), hour: Number(r.hour) }
    : parseTmyTime(r.time || r['time(UTC)'] || r.Time);
  if (!crudo) return null;
  const tm = yaSolar ? crudo : aHoraSolar(crudo, lon);
  const n = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
  return {
    ...tm,
    ghi: n(r.ghi ?? r['G(h)'] ?? r.G_h),
    dni: n(r.dni ?? r['Gb(n)'] ?? r.Gb_n),
    dhi: n(r.dhi ?? r['Gd(h)'] ?? r.Gd_h),
    tamb: n(r.tamb ?? r.t2m ?? r.T2m, 25),
  };
}

export async function fetchPVGISTMY({ lat, lon }) {
  if (lat == null || lon == null) throw new Error('PVGIS TMY: faltan lat/lon');
  const key = `${CACHE_PREFIX}tmy:${(+lat).toFixed(2)}:${(+lon).toFixed(2)}`;
  const cached = readCache(key);
  if (cached?.hourly?.length) return { ...cached, cached: true };

  // 🔴 Lo que devuelva el backend se NORMALIZA antes de usarlo. Este escalón entregaba las filas
  // tal cual —`{time, t2m}`— y el motor lee `{doy, hour, month, tamb}`: el resultado era `NaN` con
  // badge de éxito. El parseo vivía solo en la rama de respaldo de abajo, que en producción no
  // corre porque el hub responde primero. Normalizar acá vale para cualquier servidor, viejo o nuevo.
  let data = null;
  if (hubConfigurado()) {
    try {
      const d = await hubPost('pvgis-tmy', { lat, lon });
      const filas = Array.isArray(d?.hourly) ? d.hourly.map((f) => normalizarFilaTmy(f, lon)).filter(Boolean) : [];
      data = filas.length ? { ...d, ok: true, hourly: filas } : null;
    } catch { data = null; }
  }
  if (!data) {
    const qs = new URLSearchParams({ lat, lon, outputformat: 'json' });
    const res = await fetch(`https://re.jrc.ec.europa.eu/api/v5_2/tmy?${qs}`);
    if (!res.ok) throw new Error(`PVGIS TMY HTTP ${res.status}`);
    const j = await res.json();
    // Misma normalización que el escalón del hub: una sola implementación, imposible que diverjan.
    const hourly = (j.outputs?.tmy_hourly || []).map((f) => normalizarFilaTmy(f, lon)).filter(Boolean);
    data = { ok: hourly.length > 0, hourly, source: 'pvgis-tmy-direct' };
  }
  if (data?.hourly?.length) writeCache(key, data);
  return { ...data, cached: false };
}
